import { prefetchModule } from './lazyWithRetry';
import { decodeTokenPermissions, USER_TOKEN_KEY } from '../../utils/authTokens';

/**
 * Код разделов кабинета и админки: путь маршрута (внутри /panel) → import().
 * Одни и те же функции идут в React.lazy (App.tsx) и в предзагрузку —
 * повторный import() отдаёт уже скачанный модуль, поэтому раздел, код
 * которого скачан заранее, открывается без ожидания.
 */
export const panelChunks = {
  'layout':               () => import('../panel/PanelLayout'),
  'tickets':              () => import('../../page/tickets/MyTickets'),
  'tickets/new':          () => import('../../page/tickets/NewTicket'),
  'tickets/:id':          () => import('../../page/tickets/MyTicketDetail'),
  'profile':              () => import('../../page/panel/profile/PanelProfile'),
  'topup':                () => import('../../page/panel/topup/PanelTopup'),
  'access-denied':        () => import('../../page/panel/access-denied/AccessDeniedPage'),
  'consent':              () => import('../../page/panel/consent/ConsentPage'),
  'steam-callback':       () => import('../../page/panel/steam-callback/SteamCallback'),
  'admin/dashboard':      () => import('../../page/admin/dashboard/AdminDashboard'),
  'admin/chat':           () => import('../../page/admin/chat/AdminChatPage'),
  'admin/tasks':          () => import('../../page/admin/tasks/AdminTasksPage'),
  'admin/tickets':        () => import('../../page/admin/tickets/AdminTicketsPage'),
  'admin/map':            () => import('../../page/admin/map/AdminMap'),
  'admin/users':          () => import('../../page/admin/users/AdminUsers'),
  'admin/users/:id':      () => import('../../page/admin/users/AdminUserDetail'),
  'admin/roles':          () => import('../../page/admin/roles/AdminRoles'),
  'admin/documents':      () => import('../../page/admin/documents/AdminDocuments'),
  'admin/settings':       () => import('../../page/admin/settings/AdminSettings'),
  'admin/oidc-clients':   () => import('../../page/admin/oidc/AdminOidcClients'),
  'admin/rules':          () => import('../../page/admin/rules/AdminRules'),
  'admin/faq':            () => import('../../page/admin/faq/AdminFaq'),
  'admin/logs':           () => import('../../page/admin/logs/LogsShell'),
  'admin/logs/site':      () => import('../../page/admin/logs/AdminLogsSite'),
  'admin/logs/auth':      () => import('../../page/admin/logs/AdminLogsAuth'),
  'admin/logs/money':     () => import('../../page/admin/logs/AdminLogsMoney'),
  'admin/logs/vehicles':  () => import('../../page/admin/logs/AdminLogsVehicles'),
  'admin/logs/resources': () => import('../../page/admin/logs/AdminLogsResources'),
  'admin/logs/bpla':      () => import('../../page/admin/logs/AdminLogsBpla'),
  'admin/logs/momma-heart': () => import('../../page/admin/logs/AdminLogsMommaHeart'),
  'admin/logs/territory': () => import('../../page/admin/logs/AdminLogsTerritory'),
  'admin/logs/events':    () => import('../../page/admin/logs/AdminLogsEvents'),
  'admin/logs/kills':     () => import('../../page/admin/logs/AdminLogsKills'),
  'admin/logs/comms':     () => import('../../page/admin/logs/comms/AdminLogsComms'),
  'admin/logs/casino':    () => import('../../page/admin/logs/AdminLogsCasino'),
  'admin/files':          () => import('../../page/admin/files/AdminFiles'),
  'admin/pages':          () => import('../../page/admin/pages/AdminPages'),
  'admin/news':           () => import('../../page/admin/news/AdminNews'),
  'admin/launcher':       () => import('../../page/admin/launcher/AdminLauncher'),
  'admin/errors':         () => import('../../page/admin/errors/AdminErrors'),
  'admin/incidents':      () => import('../../page/admin/incidents/AdminIncidents'),
  'admin/multiaccounts':  () => import('../../page/admin/multiacc/AdminMultiacc'),
  'admin/bans':           () => import('../../page/admin/bans/AdminBans'),
  'admin/server':         () => import('../../page/admin/server/AdminServer'),
  'admin/vehicles':       () => import('../../page/admin/vehicles/AdminVehicles'),
  'admin/players':        () => import('../../page/admin/players/AdminPlayer'),
  'admin/fractions':      () => import('../../page/admin/fractions/AdminFractions'),
  'admin/fraction':       () => import('../../page/admin/fractions/AdminFraction'),
  'admin/promocodes':     () => import('../../page/admin/promocodes/AdminPromocodes'),
  'admin/finance':        () => import('../../page/admin/finance/AdminFinance'),
  'admin/online':         () => import('../../page/admin/online/AdminOnline'),
  'admin/stats':          () => import('../../page/admin/stats/AdminStats'),
} as const;

export type TPanelChunk = keyof typeof panelChunks;

/** Путь адресной строки → ключ раздела (или null, если раздела нет) */
export function chunkForPath(pathname: string): TPanelChunk | null {
  const p = pathname.replace(/^\/panel\/?/, '').replace(/\/+$/, '');
  if (p in panelChunks) return p as TPanelChunk;
  if (/^tickets\/[^/]+$/.test(p)) return 'tickets/:id';
  if (/^admin\/users\/[^/]+$/.test(p)) return 'admin/users/:id';
  return null;
}

const requested = new Set<TPanelChunk>();

/** Скачать код раздела заранее (наведение на пункт меню, простой браузера) */
export function prefetchPanelChunk(key: TPanelChunk): void {
  if (requested.has(key)) return;
  requested.add(key);
  prefetchModule(() => panelChunks[key]().catch((e: unknown) => { requested.delete(key); throw e; }));
}

export function prefetchPanelPath(pathname: string): void {
  const key = chunkForPath(pathname);
  if (key) prefetchPanelChunk(key);
  // Журнал логов открывается внутри общей шапки раздела — нужна и она
  if (key?.startsWith('admin/logs/')) prefetchPanelChunk('admin/logs');
}

// Порядок фоновой загрузки: сначала то, куда переходят чаще всего.
// Тяжёлые и редкие разделы (карта с leaflet, лаунчер, журналы) — только по наведению.
const PLAYER_IDLE: TPanelChunk[] = ['tickets', 'profile', 'tickets/:id', 'tickets/new'];
const ADMIN_IDLE: TPanelChunk[] = [
  'admin/dashboard', 'admin/tickets', 'admin/tasks', 'admin/chat', 'admin/users', 'admin/users/:id',
  'admin/logs', 'admin/multiaccounts', 'admin/bans', 'admin/roles', 'admin/news', 'admin/pages',
  'admin/rules', 'admin/faq', 'admin/stats', 'admin/online', 'admin/finance', 'admin/promocodes',
  'admin/documents', 'admin/settings',
];

type TIdleWindow = Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };

/**
 * Предзагрузка кода разделов панели: по одному чанку в простое браузера
 * после входа и по наведению/фокусу на любую ссылку /panel/*.
 * Возвращает функцию отписки.
 */
export function startPanelPrefetch(): () => void {
  const onIntent = (e: Event) => {
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a) return;
    const url = new URL(a.href, window.location.href);
    if (url.origin === window.location.origin && url.pathname.startsWith('/panel')) prefetchPanelPath(url.pathname);
  };
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('touchstart', onIntent, { passive: true });

  let cancelled = false;
  let timer = 0;
  const token = (() => { try { return localStorage.getItem(USER_TOKEN_KEY); } catch { return null; } })();
  if (token) {
    const queue = [...PLAYER_IDLE, ...(decodeTokenPermissions(token).length > 0 ? ADMIN_IDLE : [])];
    const idle = (window as TIdleWindow).requestIdleCallback;
    const next = () => {
      if (cancelled) return;
      const key = queue.shift();
      if (!key) return;
      prefetchPanelChunk(key);
      timer = window.setTimeout(schedule, 250);
    };
    const schedule = () => { if (!cancelled) { if (idle) idle(next, { timeout: 3000 }); else next(); } };
    // Первые секунды — запросам самого экрана
    timer = window.setTimeout(schedule, 2500);
  }

  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    document.removeEventListener('pointerover', onIntent);
    document.removeEventListener('focusin', onIntent);
    document.removeEventListener('touchstart', onIntent);
  };
}
